import cn from 'classnames';
import moment from 'moment';
import { ProgramStack, ProgramText, ProgramTitle } from 'planby';
import { useMemo } from 'react';
import { Button, FlexWrapper, FontIcon } from '@intigral/shared';
import { FlexChild } from '@intigral/shared/components/FlexWrapper';
import { get } from '@intigral/utils/lib/browser-storage/localStorage';
import { STORE_NAME } from '@intigral/constant';
import { IProgram } from '../interface/IProgram';
import { isFutureProgram } from '../isFutureProgram';
import cStyles from '../tvguide.module.sass';
import channelRowStyles from './channel-row.module.sass';

const Program = ({
    program,
    reminderData,
    excludeIdFromUrl,
    selectedProgram,
    lang,
    formatTime,
    liveTag,
    beforeTag,
    afterTag,
    set12HoursTimeFormat,
    onModalOpenClick,
}: IProgram) => {
    const pTimestamp = new Date(program.since).getTime();
    const pIsBeforeNow = pTimestamp < Date.now();
    const pIsAfterNow = pTimestamp > Date.now();
    const remindersList: any = useMemo(() => (reminderData ? Object.values(reminderData) : []), [reminderData]);
    const paId = excludeIdFromUrl(program?.paId);
    const listingId = excludeIdFromUrl(program?.listingId);
    const isReminded = useMemo(
        () =>
            reminderData &&
            remindersList.some(
                (reminder: any) => reminder.paId == paId && (listingId ? reminder.listingId == listingId : true)
            ),
        [reminderData, remindersList, paId, listingId]
    );

    const subscriptionGUID = program?.channel?.subscriptions?.[0]?.subscriptionGUID;

    // Check if content is purchased by comparing channel subscriptionGUID with user's PPV list
    const isPurchased = useMemo(() => {
        const userInfo = get(STORE_NAME.USER);
        const userPpvList = userInfo?.ppvList || [];
        return subscriptionGUID && userPpvList.some((ppv: any) => ppv.guid === subscriptionGUID);
    }, [program?.channel?.subscriptions]);

    let isLockedProgram = Boolean(program?.channel?.locked || program?.channel?.isLockedEpg || program?.locked);

    // Only apply this condition if the channel has a PPV subscriptionGUID
    if (subscriptionGUID && subscriptionGUID.includes('PPV')) {
        isLockedProgram = !isPurchased;
    }
    const is12HoursFormat = set12HoursTimeFormat();

    const date =
        lang === 'ar'
            ? `${(() => {
                  try {
                      // TODO: check if Arabic locale. And if not whether it's not causing performance issues
                      return moment(program.since).locale('ar').format('MMM DD');
                  } catch (error) {
                      console.warn('Arabic locale not available, falling back to English:', error);
                      return moment(program.since).locale('en').format('MMM DD');
                  }
              })()}`
            : `${moment(program.since).format('MMM DD')}`;
    // `${moment(program.since).format('MMM DD')}`;

    return (
        <ProgramStack
            // isRTL={isRTL}
            className={cn(channelRowStyles.stack, {
                [channelRowStyles.selected]:
                    selectedProgram?.id === program?.id && selectedProgram?.startTime === program?.startTime,
            })}
        >
            <>
                {program.isLive && <span className={cn(cStyles.tag, cStyles.live)}>{liveTag}</span>}
                {pIsBeforeNow && !program.isLive && <span className={cStyles.tag}>{beforeTag}</span>}
                {pIsAfterNow && <span className={cStyles.tag}>{afterTag}</span>}
                {isLockedProgram && (
                    <span className={cStyles.lock}>
                        <FontIcon name="lock" />
                    </span>
                )}
                {isFutureProgram(program?.startTime.toString(), program?.endTime.toString()) && isReminded && (
                    <span className={cStyles.reminder}>
                        <FontIcon name="bell" />
                    </span>
                )}
                <FlexWrapper justifyContent="between">
                    <FlexChild style={{ maxWidth: '90%' }}>
                        {/* TODO: check if cStyles are needed and if not update channelRowStyles */}
                        <ProgramTitle className={cn(cStyles.prgTitle, channelRowStyles.prgTitle)}>
                            {lang === 'en' ? program.title : program?.localizedTitle?.ar}
                        </ProgramTitle>
                        {/* TODO: check if cStyles are needed and if not update channelRowStyles */}
                        <ProgramText className={cn(cStyles.prgTime, channelRowStyles.prgTime)}>
                            {date}
                            {', '}
                            {formatTime(program.since.toString(), is12HoursFormat).toLowerCase()} -{' '}
                            {formatTime(program.till.toString(), is12HoursFormat).toLowerCase()}
                        </ProgramText>
                    </FlexChild>
                    <FlexChild>
                        <Button
                            plain
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                onModalOpenClick(program);
                            }}
                            className={channelRowStyles['details-button']}
                        >
                            <FontIcon name="submenu-vertical" className={channelRowStyles['details-icon']} />
                        </Button>
                    </FlexChild>
                </FlexWrapper>
            </>
        </ProgramStack>
    );
};

export default Program;
